import React, { useState, useEffect, useMemo } from 'react';
import { Radio, Input, Button, Select } from '@/src/components/Antd';
import SectionWrapper from '@/src/components/Layouts/Section/SectionWrapper';
import { EPlanType, EPlanStatus, CN_REGION } from '@/src/types/common';
import { UNIFY_OFFICIAL_LINK } from '@/src/tools/constant';
import { useI18n } from 'src/hooks/useRouterChange';
import { i18nGet } from 'src/tools/tools';
import { replaceI18nVariables } from '@/src/tools/tools';
import Configuration from '@/src/components/Configuration/Configuration';

import css from './css.module.less';

interface PriceItem {
  deleted?: boolean;
  unitAmount: number | string;
  frequencyUnit: string;
  currency: string;
  stripePriceId?: string;
}

interface UpdatePlanProps {
  planType: EPlanType;
  planStatus: EPlanStatus;
  storeRegion: string;
  prices: PriceItem[];
  handleUpdatePlan: (stripePriceId?: string | null) => void;
  handleEnterpriseSubmit: (code: string) => Promise<any>;
  disabled?: boolean;
  updateLoading?: boolean;
}

/** 计算可选计划列表，管理选中项与默认选中 */
function usePlanSelection(
  planType: EPlanType,
  planStatus: EPlanStatus,
  storeRegion: string,
  getI18n: (key: string) => string,
) {
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);

  const radioList = useMemo(() => {
    const basicPlanOption = {
      value: EPlanType.BASIC,
      label: getI18n('storePlanAndBilling.index.basicPlan'),
    };
    const enterprisePlanOption = {
      value: EPlanType.ENTERPRISE,
      label: getI18n('storePlanAndBilling.index.enterprisePlan'),
    };
    const bothPlans = [basicPlanOption, enterprisePlanOption];

    const isCN = storeRegion === CN_REGION;

    let shouldShowBothPlans = false;
    let shouldShowEnterprise = false;
    let shouldShowBasicPlan = false;

    switch (planType) {
      case EPlanType.TRIAL: {
        shouldShowBothPlans = true;
        break;
      }
      case EPlanType.BASIC: {
        if (
          planStatus === EPlanStatus.CANCELLED ||
          planStatus === EPlanStatus.EXPIRED
        ) {
          shouldShowBothPlans = true;
        }

        if (
          planStatus === EPlanStatus.ACTIVE ||
          planStatus === EPlanStatus.PENDING
        ) {
          shouldShowEnterprise = true;
        }

        break;
      }
      case EPlanType.ENTERPRISE: {
        if (!isCN && planStatus === EPlanStatus.EXPIRED) {
          shouldShowBothPlans = true;
        }

        if (!isCN && planStatus === EPlanStatus.ACTIVE) {
          shouldShowBasicPlan = true;
        }

        if (isCN && planStatus !== EPlanStatus.ACTIVE) {
          shouldShowEnterprise = true;
        }
        break;
      }
      case EPlanType.NONE: {
        if (isCN && planStatus === EPlanStatus.NONE) {
          shouldShowEnterprise = true;
        }
      }
      default:
        break;
    }

    if (shouldShowBothPlans) {
      return bothPlans;
    }

    if (shouldShowEnterprise) {
      return [enterprisePlanOption];
    }

    if (shouldShowBasicPlan) {
      return [basicPlanOption];
    }

    return [];
  }, [planType, planStatus, storeRegion, getI18n]);

  useEffect(() => {
    if (!radioList.length) return;
    const existsInList = radioList.some((i) => i.value === selectedPlan);
    if (!existsInList) {
      setSelectedPlan(radioList[0].value);
    }
  }, [radioList, selectedPlan]);

  return { selectedPlan, setSelectedPlan, radioList };
}

/** 计算价格选项列表与折扣，管理选中的 stripePriceId 与默认选中 */
function usePriceSelection(
  prices: PriceItem[],
  storeRegion: string,
  getI18n: (key: string) => string,
) {
  const [selectedStripePriceId, setSelectedStripePriceId] = useState<
    string | null
  >(null);

  const yearPrice = useMemo(
    () => prices?.find((p) => p.frequencyUnit === 'Year'),
    [prices],
  );
  const monthPrice = useMemo(
    () => prices?.find((p) => p.frequencyUnit === 'Month'),
    [prices],
  );

  const discount = useMemo(() => {
    if (!yearPrice || !monthPrice) return 0;
    const yearAmount = parseFloat(String(yearPrice.unitAmount ?? '0'));
    const monthAmount = parseFloat(String(monthPrice.unitAmount ?? '0'));
    if (monthAmount === 0) return 0;
    const yearlyFromMonthly = monthAmount * 12;
    const value = ((yearlyFromMonthly - yearAmount) / yearlyFromMonthly) * 100;
    return Math.round(value * 10) / 10;
  }, [yearPrice, monthPrice]);

  const priceRadioList = useMemo(() => {
    if (storeRegion === CN_REGION) return [];
    return (
      prices
        ?.filter((price) => !price.deleted && price.stripePriceId)
        .map((price) => {
          const amount = parseFloat(String(price.unitAmount ?? '0'));
          let detail = `${amount} ${price.currency}/${price.frequencyUnit}`;
          const frequencyUnitLabelMap: Record<string, string> = {
            Year: getI18n('storePlanAndBilling.index.yearly'),
            Month: getI18n('storePlanAndBilling.index.monthly'),
            Day: getI18n('storePlanAndBilling.index.daily'),
          };
          if (price.frequencyUnit === 'Year') {
            detail = replaceI18nVariables(
              getI18n('storePlanAndBilling.index.priceYear'),
              amount,
              price.currency,
              discount,
            );
          } else if (price.frequencyUnit === 'Month') {
            detail = replaceI18nVariables(
              getI18n('storePlanAndBilling.index.priceMonth'),
              amount,
              price.currency,
            );
          }
          return {
            value: price.stripePriceId as string,
            label:
              frequencyUnitLabelMap[price.frequencyUnit] ?? price.frequencyUnit,
            detail,
          };
        }) ?? []
    );
  }, [prices, storeRegion, getI18n, discount]);

  const selectedPriceDetail = useMemo(() => {
    const found = priceRadioList.find(
      (item) => item.value === selectedStripePriceId,
    );
    return found?.detail ?? '';
  }, [priceRadioList, selectedStripePriceId]);

  useEffect(() => {
    if (!selectedStripePriceId && priceRadioList.length > 0) {
      setSelectedStripePriceId(priceRadioList[0].value);
    }
  }, [priceRadioList, selectedStripePriceId]);

  return {
    selectedStripePriceId,
    setSelectedStripePriceId,
    priceRadioList,
    yearPrice,
    discount,
    selectedPriceDetail,
  };
}

const UpdatePlanSection = ({
  planType,
  planStatus,
  storeRegion,
  prices,
  handleUpdatePlan,
  handleEnterpriseSubmit,
  disabled,
  updateLoading,
}: UpdatePlanProps) => {
  const i18n = useI18n();
  const getI18n = (str: string) => i18nGet(i18n, str);

  const { selectedPlan, setSelectedPlan, radioList } = usePlanSelection(
    planType,
    planStatus,
    storeRegion,
    getI18n,
  );
  const {
    selectedStripePriceId,
    setSelectedStripePriceId,
    priceRadioList,
    selectedPriceDetail,
  } = usePriceSelection(prices, storeRegion, getI18n);

  const [licenceCode, setLicenceCode] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const handlePlanChange = (e) => {
    setSelectedPlan(e.target.value);
    if (e.target.value !== EPlanType.ENTERPRISE) {
      setLicenceCode('');
      setErrorMessage('');
    }
  };

  const handlePriceChange = (value: string) => {
    setSelectedStripePriceId(value);
  };

  const handleLicenceCodeChange = (value: string) => {
    setLicenceCode(value);
    if (errorMessage) setErrorMessage('');
  };

  const handleSubmit = async () => {
    try {
      setErrorMessage('');
      await handleEnterpriseSubmit(licenceCode);
      setLicenceCode('');
    } catch (error) {
      setErrorMessage(
        getI18n('storePlanAndBilling.index.pleaseentervalidcode'),
      );
    }
  };

  const basicPlanFeatures = [
    getI18n('storePlanAndBilling.index.free3saleschannels'),
    getI18n('storePlanAndBilling.index.renewSSL'),
    getI18n('storePlanAndBilling.index.unlimitedproduct'),
    getI18n('storePlanAndBilling.index.unlimitedcontent'),
  ];

  return (
    <SectionWrapper
      titleClassName={css.titleContainerAll}
      title={getI18n('storePlanAndBilling.index.updateYourPlan')}
      className={css.storeDetailsInfo}>
      <div className={css.updatePlanContainer}>
        <div className={css.radioGroup}>
          {radioList.map((item) => {
            const isBasicSelected = selectedPlan === EPlanType.BASIC;
            const isEnterpriseSelected = selectedPlan === EPlanType.ENTERPRISE;

            const showBasicContent =
              isBasicSelected &&
              item.value === EPlanType.BASIC &&
              storeRegion !== CN_REGION;

            const showEnterpriseContent =
              isEnterpriseSelected && item.value === EPlanType.ENTERPRISE;

            return (
              <div key={item.value} className={css.radioItem}>
                <Radio
                  radioList={[item]}
                  value={selectedPlan}
                  onChange={handlePlanChange}
                  disabled={disabled}
                  className={css.planRadio}
                />

                {/* Basic Plan 内容 */}
                {showBasicContent && (
                  <div className={css.planContent}>
                    <div className={css.priceSection}>
                      {priceRadioList.length > 0 && (
                        <div className={css.radioGroup}>
                          <div className={css.billingCycleLabel}>
                            {getI18n('storePlanAndBilling.index.billingCycle')}
                          </div>
                          <Select
                            optionsList={priceRadioList}
                            value={selectedStripePriceId}
                            onChange={handlePriceChange}
                            disabled={disabled}
                            style={{ width: '50%' }}
                            size='large'
                          />
                        </div>
                      )}

                      {selectedPriceDetail && (
                        <div className={css.mainPrice}>
                          {selectedPriceDetail}
                        </div>
                      )}
                    </div>

                    <div className={css.featuresList}>
                      {basicPlanFeatures.map((feature, index) => (
                        <div key={index} className={css.featureItem}>
                          {feature}
                        </div>
                      ))}
                    </div>

                    <Button
                      type='primary'
                      className={css.updateButton}
                      onClick={() => {
                        handleUpdatePlan(selectedStripePriceId);
                      }}
                      disabled={
                        updateLoading || disabled || !selectedStripePriceId
                      }>
                      {getI18n('storePlanAndBilling.index.updateNow')}
                    </Button>
                  </div>
                )}

                {/* Enterprise Plan 内容 */}
                {showEnterpriseContent && (
                  <div className={css.planContent}>
                    <div className={css.enterpriseHint}>
                      <Configuration
                        configStart={getI18n(
                          'storePlanAndBilling.index.licenseCodeDesc',
                        )}
                        href={UNIFY_OFFICIAL_LINK}
                        configLink={getI18n(
                          'storePlanAndBilling.index.licenseCodeDescLink',
                        )}
                        configEnd={getI18n(
                          'storePlanAndBilling.index.licenseCodeDescEnd',
                        )}
                        hasIcon={false}
                        hideGt={true}
                      />
                    </div>

                    <div className={css.licenceInputWrapper}>
                      <label className={css.licenceLabel}>
                        {getI18n('storePlanAndBilling.index.licenceCode')}
                        <span className={css.required}>*</span>
                      </label>
                      <Input
                        size='large'
                        value={licenceCode}
                        onChange={handleLicenceCodeChange}
                        disabled={disabled}
                        className={css.licenceInput}
                        help={errorMessage}
                        error={!!errorMessage}
                      />
                    </div>

                    <Button
                      type='primary'
                      className={css.submitButton}
                      onClick={handleSubmit}
                      disabled={disabled || !licenceCode}>
                      {getI18n('general.submit')}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </SectionWrapper>
  );
};

export default UpdatePlanSection;
